import * as ko from 'knockout';

import i18n from '../i18n';
import { BarChartConfig, isLegendClick } from '../ko_bindings/bar_chart';
import {
  getCropSearchConfig,
  getCustomerSearchConfig,
  getCountrySearchConfig,
  getStageSearchConfig,
  getBreederSearchConfig,
  getProjectSearchConfig,
} from '../components/configs/search_configs';
import {
  kpi,
  OverviewParams,
  OverviewChartData,
  OverviewKPIData,
  trialLocations,
  AllRecordsData,
  TrialledRecordsData,
  StageTransitionsData,
  allRecords,
  trialledRecords,
  stageTransitions,
  trialsCharts,
  recordsCharts,
  allRecordsExport,
  trialledRecordsExport,
  stageTransitionsExport,
  trialsChartsExport,
  PortfolioStatusData,
  portfolioStatus,
  RecordsChartsData,
  OverviewChartLegend,
} from '../api/overview';
import { downloadBlob, updateLocationWithQueryString } from '../utils';
import * as dimensionsApi from '../api/dimensions';
import * as dimensionMetasApi from '../api/dimension_metas';
import { CROP_VARIETY_SLUG } from '../api/dimension_metas';
import { CountryData, countriesApi } from '../api/countries';
import * as cropsApi from '../api/crops';
import * as tppsApi from '../api/tpps';
import { GeoJSON } from '../api/datasets';
import { getTrialLocationsMap } from './trials';
import { serializeDate, parseDate, deflateList } from '../api/serialization';
import { session } from '../session';
import { customersApi, CustomerData, BreederData, breedersApi } from '../api/organizations';
import { ProjectData, projectApi } from '../api/projects';
import { FormSelectSearchConfiguration } from '../components/form_select_search';
import { openRecordStageChangeChart } from '../components/record_stage_change_chart';
import { I18nText, translate } from '../i18n_text';
import {
  NON_DRAFT_TRIAL_STATES_WITHOUT_APPROVED,
  TrialState,
  getTrialStateChoices,
} from '../models/TrialState';
import { FilterDelegate, IdData } from '../components/list_filters';

let template = require('raw-loader!../../templates/overview.html').default;

type OverviewLinkParams = {
  [P in keyof OverviewParams]: string;
} & {
  selected: string;
};

const RENAMED_TABS: { [oldName: string]: string } = {
  varieties_charts: 'records_charts',
  all_varieties: 'all_records',
  trialled_varieties: 'trialled_records',
};

function getYearOptions() {
  let res = [{ name: i18n.t('All')(), value: 'all' }];
  for (let i = 0; i < 10; i++) {
    let year = (new Date().getFullYear() - i).toString();
    res.push({ name: year, value: year });
  }

  return res;
}

function setYearOption(obs: KnockoutObservable<string>, value: string) {
  if (value === 'all' || !isNaN(parseInt(value, 10))) {
    obs(value);
  }
}

function toYearValue(obs: KnockoutObservable<string>) {
  return obs() === 'all' ? null : obs();
}

function asChartConfig(
  obs: ko.MaybeObservableArray<OverviewChartData>
): (BarChartConfig & { legends: OverviewChartLegend[] })[] {
  return ko.unwrap(obs).map((data) => {
    return {
      type: data.type,
      title: data.title,
      aspectRatio: data.aspect_ratio ?? 2,
      xTitle: data.x_title,
      fontSize: 12,
      timeUnit: 'month' as 'month',
      labels: data.x_labels,
      datasets: data.data,
      hideLegend: !!data.legends,
      // These charts can have many series (e.g. one per country/stage). Keep the built-in legend
      // below the chart so it never overlaps the title.
      legendPosition: 'bottom',
      legends: data.legends,
      hiddenDatasetIndexes: ko.observableArray<number>(),
    };
  });
}

class OverviewScreen {
  private requestNumber = 0;
  private requestNumberKPI = 0;
  private loadMoreAllRecordsTimeout: number | null = null;

  mapVisualizationFeatureEnabled = session.tenant() && session.tenant().map_visualization_enabled;
  showTPP = session.tenant().tpp_enabled;
  showS2bim = SERVER_INFO.ENABLE_S2BIM;

  loadingFilters = ko.observable(true);
  loading = ko.observable(false);
  loadingKPI = ko.observable(false);
  selected = ko.observable<
    | 'trials_charts'
    | 'records_charts'
    | 'map'
    | 'all_records'
    | 'trialled_records'
    | 'stage_transitions'
    | 'portfolio_status'
  >('trials_charts');
  trialsChartsData = ko.observableArray<OverviewChartData>();
  recordsChartsData = ko.observable<RecordsChartsData>();
  mapData = ko.observableArray<GeoJSON>();
  allRecordsData = ko.observable<AllRecordsData>();
  loadingMoreText = i18n.t('Loading more...')();
  allRecordsLimit = ko.observable(100);
  allRecordsOffset = ko.observable(0);
  allRecordsLoadingMore = ko.observable(false);
  allRecordsHasMore = ko.observable(false);
  trialledRecordsData = ko.observable<TrialledRecordsData>();
  stageTransitionsData = ko.observable<StageTransitionsData>();
  portfolioStatusData = ko.observable<PortfolioStatusData>();

  exportingText = i18n.t('Exporting...')();
  exportingAllRecords = ko.observable(false);
  exportingTrialledRecords = ko.observable(false);
  exportingStageTransitions = ko.observable(false);
  exportingTrialsChartsData = ko.observable(false);

  kpiData = ko.observableArray<OverviewKPIData>();

  fromPlantingDate = ko.observable<Date>(null);
  toPlantingDate = ko.observable<Date>(null);
  crop = ko.observable<cropsApi.CropData>(null);
  commercial = ko.observable<'yes' | 'no' | 'both'>('both');
  customer = ko.observable<CustomerData>(null);
  cropVarietyName = ko.observable('');
  publicCV = ko.observable<'yes' | 'no' | 'mixed' | 'any'>('any');
  cvStage = ko.observable<dimensionsApi.DimensionData>(null);
  cvCountry = ko.observable<CountryData>(null);
  owner = ko.observable<BreederData>(null);
  trialYear = ko.observable('all');
  cvStageYear = ko.observable('all');
  cvControl = ko.observable<'yes' | 'no' | 'both'>('both');
  external = ko.observable<'yes' | 'no' | 'both'>('both');
  tpp = ko.observable<tppsApi.TPPListData>(null);
  project = ko.observable<ProjectData>(null);
  // Which dimension type the stage analytics describe; null means crop varieties.
  dimensionMeta = ko.observable<dimensionMetasApi.DimensionMetaData>(null);

  private trialStateChoices = getTrialStateChoices().filter((s) =>
    NON_DRAFT_TRIAL_STATES_WITHOUT_APPROVED.includes(s.id)
  );

  trialStateFilter = ko.observableArray<IdData>(
    this.trialStateChoices.filter((s) => [TrialState.Active, TrialState.DataValidated].includes(s.id))
  );

  trialStateFilters: FilterDelegate[] = [
    {
      title: i18n.t('State')(),
      entities: this.trialStateFilter,
      list: (params) => Promise.resolve(this.trialStateChoices),
    },
  ];

  yearOptions = getYearOptions();

  boolOptions = [
    { name: i18n.t('Yes')(), value: 'yes' },
    { name: i18n.t('No')(), value: 'no' },
    { name: i18n.t('Both')(), value: 'both' },
  ];
  publicOptions = [
    { name: i18n.t('Yes')(), value: 'yes' },
    { name: i18n.t('No')(), value: 'no' },
    { name: i18n.t('Mixed')(), value: 'mixed' },
    { name: i18n.t('Any')(), value: 'any' },
  ];
  ownerOptions = ko.observableArray<{ name: string; value: string }>();
  cropSearchConfig = getCropSearchConfig(this.crop, { disableCreate: true });
  customerSearchConfig = getCustomerSearchConfig(this.customer, {
    disableCreate: true,
  });
  cvCountrySearchConfig = getCountrySearchConfig(this.cvCountry, {
    disableCreate: true,
  });
  cvStageSearchConfig = getStageSearchConfig(this.cvStage, {
    disableCreate: true,
  });
  ownerSearchConfig = getBreederSearchConfig(this.owner, true);
  dimensionMetaSearchConfig: FormSelectSearchConfiguration<dimensionMetasApi.DimensionMetaData> = {
    getSummaryName: (dimensionMeta) => dimensionMeta.name_json,
    list: (params) =>
      dimensionMetasApi.listExcludingDates({ ...params, usage: 'any', supports_stages: true }),
    entity: this.dimensionMeta,
  };

  // Crop, owner/breeder and public only exist for crop varieties.
  isCropVariety = ko.pureComputed(
    () => !this.dimensionMeta() || this.dimensionMeta().slug === CROP_VARIETY_SLUG
  );

  // Labels name the charted dimension type; crop varieties keep their original wording so the
  // existing translations still apply. The pair of `i18n.t` calls is repeated per label on
  // purpose: the extractor (localizer's tools/knockout/collect_translatable_strings.js) only
  // collects string literals passed directly to `.t(…)`, so factoring the ternary into a
  // helper taking key names would drop every one of these strings from the catalog.
  private dimensionName = ko.pureComputed(() => {
    const dimensionMeta = this.dimensionMeta();
    return dimensionMeta ? translate(dimensionMeta.name_json) : '';
  });

  recordsChartsTabLabel = ko.pureComputed(() =>
    this.isCropVariety()
      ? i18n.t('Varieties Charts')()
      : i18n.t('{{ name }} charts', { name: this.dimensionName() })()
  );

  allRecordsTabLabel = ko.pureComputed(() =>
    this.isCropVariety()
      ? i18n.t('All varieties')()
      : i18n.t('All {{ name }}', { name: this.dimensionName() })()
  );

  trialledRecordsTabLabel = ko.pureComputed(() =>
    this.isCropVariety()
      ? i18n.t('Trialled varieties')()
      : i18n.t('Trialled {{ name }}', { name: this.dimensionName() })()
  );

  stageTransitionsTabLabel = ko.pureComputed(() =>
    this.isCropVariety()
      ? i18n.t('Stage transitions')()
      : i18n.t('{{ name }} stage transitions', { name: this.dimensionName() })()
  );

  /** Header of the first column of the all-records table. */
  recordLabel = ko.pureComputed(() =>
    this.isCropVariety() ? i18n.t('Variety')() : this.dimensionName()
  );

  /** Header of the count column of the stage-transitions table. */
  recordCountLabel = ko.pureComputed(() =>
    this.isCropVariety()
      ? i18n.t('N. Varieties')()
      : i18n.t('N. {{ name }}', { name: this.dimensionName() })()
  );
  tppSearchConfig: FormSelectSearchConfiguration<tppsApi.TPPListData> = {
    getSummaryName: (tpp) => tpp.name_json,
    list: (params) => tppsApi.list(params),
    entity: this.tpp,
  };
  projectSearchConfig = getProjectSearchConfig(this.project);

  trialsChartsConfigs = ko.pureComputed(() => asChartConfig(this.trialsChartsData));
  recordsChartsConfigs = ko.pureComputed(() => asChartConfig(this.recordsChartsData()?.charts ?? []));
  fullscreenChart = ko.observable<BarChartConfig>(null);
  downloadableChart = ko.observable<BarChartConfig>(null);
  stageChangeChart = ko.pureComputed(() => {
    if (!this.tpp()) {
      return null;
    }

    return this.recordsChartsData()?.stage_changes;
  });

  private subscriptions: KnockoutSubscription[] = [];

  constructor(params: { filters: OverviewLinkParams }) {
    let filters = params.filters;
    // The tab name is part of the shareable dashboard URL, so links created before the
    // tabs were renamed still carry the old name; map them instead of silently falling
    // back to the first tab.
    const selected = RENAMED_TABS[filters.selected] ?? filters.selected;
    if (
      [
        'trials_charts',
        'records_charts',
        'map',
        'all_records',
        'trialled_records',
        'stage_transitions',
        'portfolio_status',
      ].indexOf(selected) == -1
    ) {
      filters.selected = 'trials_charts';
    } else {
      filters.selected = selected;
    }
    this.selected(filters.selected as any);
    if (filters.commercial) {
      this.commercial(filters.commercial === 'yes' ? 'yes' : filters.commercial === 'no' ? 'no' : 'both');
    }
    if (filters.crop_variety_name) {
      this.cropVarietyName(params.filters.crop_variety_name || '');
    }
    this.cropVarietyName = this.cropVarietyName.extend({ throttle: 300 });
    if (filters.public_cv) {
      this.publicCV(
        filters.public_cv === 'yes'
          ? 'yes'
          : filters.public_cv === 'no'
            ? 'no'
            : filters.public_cv === 'mixed'
              ? 'mixed'
              : 'any'
      );
    }
    if (filters.cv_control) {
      this.cvControl(filters.cv_control === 'yes' ? 'yes' : filters.cv_control === 'no' ? 'no' : 'both');
    }
    if (filters.from_planting_date) {
      this.fromPlantingDate(parseDate(filters.from_planting_date));
    }
    if (filters.to_planting_date) {
      this.toPlantingDate(parseDate(filters.to_planting_date));
    }
    setYearOption(this.trialYear, filters.trial_year);
    setYearOption(this.cvStageYear, filters.cv_stage_year);
    if (filters.external) {
      this.external(filters.external === 'yes' ? 'yes' : filters.external === 'no' ? 'no' : 'both');
    }

    const states = filters.trial_states?.split(',').filter((s) => s);
    if (states && states.length > 0) {
      this.trialStateFilter(this.trialStateChoices.filter((s) => states.includes(s.id)));
    }

    let crop = filters.crop ? cropsApi.retrieve(filters.crop) : null;
    let customer = filters.customer ? customersApi.retrieve(filters.customer) : null;
    let cvStage = filters.cv_stage ? dimensionsApi.retrieve(null, filters.cv_stage) : null;
    let cvCountry = filters.cv_country ? countriesApi.retrieve(filters.cv_country) : null;
    let owner = filters.owner ? breedersApi.retrieve(filters.owner) : null;
    let tpp = filters.tpp ? tppsApi.retrieve(filters.tpp) : null;
    let project = filters.project ? projectApi.retrieve(filters.project) : null;
    let dimensionMeta = filters.dimension_meta_id
      ? dimensionMetasApi.retrieve(filters.dimension_meta_id)
      : null;

    Promise.all([crop, customer, cvStage, cvCountry, owner, tpp, project, dimensionMeta]).then(
      ([crop, customer, cvStage, cvCountry, owner, tpp, project, dimensionMeta]) => {
        this.crop(crop as cropsApi.CropData);
        this.customer(customer);
        this.cvStage(cvStage);
        this.cvCountry(cvCountry);
        this.owner(owner);
        this.tpp(tpp);
        this.project(project);
        this.dimensionMeta(dimensionMeta);

        this.loadingFilters(false);

        this.sub(this.fromPlantingDate);
        this.sub(this.toPlantingDate);
        this.sub(this.crop);
        this.sub(this.commercial);
        this.sub(this.customer);
        this.sub(this.cropVarietyName);
        this.sub(this.publicCV);
        this.sub(this.cvStage);
        this.sub(this.owner);
        this.sub(this.cvCountry);
        this.sub(this.trialYear);
        this.sub(this.cvStageYear);
        this.sub(this.cvControl);
        this.sub(this.external);
        this.sub(this.tpp);
        this.sub(this.project);
        this.sub(this.dimensionMeta);
        this.sub(this.trialStateFilter);

        this.sub(this.selected);

        this.onChange();
        this.onChangeKPI();
      }
    );
  }

  private sub(obs: KnockoutObservable<{}>) {
    this.subscriptions.push(obs.subscribe(this.onChange));
  }

  dispose() {
    this.clearAllRecordsTimeout();
    for (let sub of this.subscriptions) {
      sub.dispose();
    }
  }

  onChange = () => {
    if (this.selected() !== 'all_records') {
      this.clearAllRecordsTimeout();
    }

    updateLocationWithQueryString(this.toLinkData());

    this.fetch('map', trialLocations, (data) => this.mapData(getTrialLocationsMap(data)));
    this.fetch('trials_charts', trialsCharts, this.trialsChartsData);
    this.fetch('records_charts', recordsCharts, this.recordsChartsData);
    this.fetchAllRecords();
    this.fetch('trialled_records', trialledRecords, this.trialledRecordsData);
    this.fetch('stage_transitions', stageTransitions, this.stageTransitionsData);
    this.fetch('portfolio_status', portfolioStatus, this.portfolioStatusData);
  };

  private fetch<T>(tab: string, req: (params: OverviewParams) => Promise<T>, target: (param: T) => void) {
    if (this.selected() !== tab) {
      return;
    }

    let requestNumber = ++this.requestNumber;

    this.loading(true);
    req(this.toData())
      .then((data) => {
        if (requestNumber !== this.requestNumber) {
          return;
        }

        target(data);
        this.loading(false);
      })
      .catch((e) => {
        this.loading(false);
        throw e;
      });
  }

  private fetchAllRecords = (append: boolean = false) => {
    if (this.selected() !== 'all_records') {
      return;
    }

    let requestNumber = ++this.requestNumber;

    if (!append) {
      this.loading(true);
      this.allRecordsOffset(0);
      this.allRecordsData(null);
      this.allRecordsHasMore(false);
    } else {
      this.allRecordsLoadingMore(true);
    }

    const params = {
      ...this.toData(),
      limit: this.allRecordsLimit(),
      offset: this.allRecordsOffset(),
    };

    allRecords(params)
      .then((data) => {
        if (requestNumber !== this.requestNumber) {
          return;
        }

        if (append && this.allRecordsData()) {
          const existingData = this.allRecordsData();
          const updatedData: AllRecordsData = {
            ...existingData,
            items: [...existingData.items, ...data.items],
            pagination: data.pagination,
          };
          this.allRecordsData(updatedData);
        } else {
          this.allRecordsData(data);
        }

        if (data.pagination) {
          const newOffset = this.allRecordsOffset() + data.items.length;
          this.allRecordsOffset(newOffset);
          // Check if there are more items based on total_count
          this.allRecordsHasMore(newOffset < data.pagination.total_count);
        }

        this.loading(false);
        this.allRecordsLoadingMore(false);
      })
      .catch((e) => {
        this.loading(false);
        this.allRecordsLoadingMore(false);
        throw e;
      });
  };

  loadMoreAllRecords = () => {
    if (!this.loading() && !this.allRecordsLoadingMore() && this.allRecordsHasMore()) {
      this.fetchAllRecords(true);
    }
  };

  private clearAllRecordsTimeout = () => {
    if (this.loadMoreAllRecordsTimeout !== null) {
      window.clearTimeout(this.loadMoreAllRecordsTimeout);
      this.loadMoreAllRecordsTimeout = null;
    }
  };

  handleAllRecordsScroll = (container: HTMLElement) => {
    // Debounce scroll events to avoid multiple rapid calls
    if (this.loadMoreAllRecordsTimeout) {
      return;
    }

    this.loadMoreAllRecordsTimeout = window.setTimeout(() => {
      this.loadMoreAllRecordsTimeout = null;

      // Check if still on all_records tab before executing
      if (this.selected() !== 'all_records') {
        return;
      }

      const scrollThreshold = 100;
      const isNearBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight - scrollThreshold;

      if (isNearBottom) {
        this.loadMoreAllRecords();
      }
    }, 100);
  };

  onChangeKPI = () => {
    let requestNumberKPI = ++this.requestNumberKPI;
    this.loadingKPI(true);

    kpi()
      .then((data) => {
        if (requestNumberKPI !== this.requestNumberKPI) {
          return;
        }

        this.kpiData(data);
        this.loadingKPI(false);
      })
      .catch((e) => {
        this.loadingKPI(false);
        throw e;
      });
  };

  selectTrialsCharts = () => {
    this.selected('trials_charts');
  };

  selectRecordsCharts = () => {
    this.selected('records_charts');
  };

  selectMap = () => {
    this.selected('map');
  };

  selectAllRecords = () => {
    this.selected('all_records');
  };

  selectTrialledRecords = () => {
    this.selected('trialled_records');
  };

  selectStageTransitions = () => {
    this.selected('stage_transitions');
  };

  selectPortfolioStatus = () => {
    this.selected('portfolio_status');
  };

  showFullscreenChart = (chart: BarChartConfig, event: MouseEvent) => {
    if (isLegendClick(event)) {
      return;
    }
    this.downloadableChart({ ...chart, fontSize: chart.fontSize * 2 });
    this.fullscreenChart(chart);
  };

  hideFullscreenChart = () => {
    this.fullscreenChart(null);
    this.downloadableChart(null);
  };

  downloadChart = () => {
    let canvas = <HTMLCanvasElement>document.getElementById('download-chart');
    let fileName = this.fullscreenChart().title + '.png';
    if ((canvas as any).msToBlob) {
      downloadBlob((canvas as any).msToBlob(), fileName);
    } else {
      canvas.toBlob((blob) => {
        downloadBlob(blob, fileName);
      });
    }
  };

  showRecordStageChangeChart = (variety: { id: string; name: string | I18nText }) => {
    openRecordStageChangeChart(variety);
  };

  exportAllRecords = () => {
    this.export(this.exportingAllRecords, allRecordsExport, 'all_records');
  };

  exportTrialledRecords = () => {
    this.export(this.exportingTrialledRecords, trialledRecordsExport, 'trialled_records');
  };

  exportStageTransitions = () => {
    this.export(this.exportingStageTransitions, stageTransitionsExport, 'stage_transitions');
  };

  exportTrialsChartsData = () => {
    this.export(this.exportingTrialsChartsData, trialsChartsExport, 'trials_charts_data');
  };

  private export(
    obs: KnockoutObservable<boolean>,
    api: (params: OverviewParams) => Promise<Blob>,
    name: string
  ) {
    obs(true);
    api(this.toData())
      .then((data) => {
        obs(false);
        downloadBlob(data, name + '.xlsx');
      })
      .catch(() => {
        obs(false);
      });
  }

  private toLinkData() {
    let data: { [key: string]: {} } = {
      selected: this.selected(),
      ...this.toData(),
    };

    for (let k in data) {
      if (k === 'selected') {
        if (data[k] === 'trials_charts') {
          data[k] = undefined;
        }
        continue;
      }
      if (k === 'commercial' || k === 'public_cv' || k === 'cv_control') {
        if (data[k] === 'both' || data[k] === 'any') {
          data[k] = undefined;
        }
        continue;
      }
      if (k === 'trial_states') {
        let states = data[k] as string[];
        if (states) {
          data[k] = states.join(',');
        }
        continue;
      }
      if (data[k] === true) {
        data[k] = 'yes';
      }
      if (data[k] === false) {
        data[k] = undefined;
      }
    }

    return data;
  }

  private toData(): OverviewParams {
    return {
      from_planting_date: serializeDate(this.fromPlantingDate()),
      to_planting_date: serializeDate(this.toPlantingDate()),
      crop: extractId(this.crop()),
      commercial: this.commercial(),
      customer: extractId(this.customer()),
      crop_variety_name: this.cropVarietyName(),
      public_cv: this.publicCV(),
      cv_stage: extractId(this.cvStage()),
      cv_country: extractId(this.cvCountry()),
      owner: extractId(this.owner()),
      trial_year: toYearValue(this.trialYear),
      cv_stage_year: toYearValue(this.cvStageYear),
      cv_control: this.cvControl(),
      external: this.external(),
      tpp: extractId(this.tpp()),
      project: extractId(this.project()),
      dimension_meta_id: extractId(this.dimensionMeta()),
      trial_states: deflateList(this.trialStateFilter),
    };
  }
}

function extractId(x: { id?: string }) {
  return x ? x.id : null;
}

export let overview = {
  name: 'overview',
  viewModel: OverviewScreen,
  template: template,
};

ko.components.register(overview.name, overview);
